(() => {
  const VISIBLE_THUMBS = 5;

  const getFeaturedProductThumbMetrics = (sectionRoot) => {
    const mediaRoot = sectionRoot.querySelector('.featured-product__media');
    const thumbsViewport = mediaRoot?.querySelector('.product__thumbs');
    const scroller = mediaRoot?.querySelector('.product__thumbs--scroller');
    const items = scroller?.querySelectorAll('[data-product-thumb-item]');

    if (!mediaRoot || !thumbsViewport || !scroller || !items?.length) {
      return null;
    }

    const mediaStyle = getComputedStyle(mediaRoot);
    const scrollerStyle = getComputedStyle(scroller);
    const gapFromScroller = parseFloat(scrollerStyle.gap || scrollerStyle.columnGap);
    const gapFromVar = parseFloat(mediaStyle.getPropertyValue('--featured-product-thumb-gap'));
    const gap = Number.isFinite(gapFromScroller) && gapFromScroller >= 0
      ? gapFromScroller
      : (Number.isFinite(gapFromVar) && gapFromVar >= 0 ? gapFromVar : 16);
    const viewportWidth = thumbsViewport.clientWidth;
    const thumbSize = (viewportWidth - gap * (VISIBLE_THUMBS - 1)) / VISIBLE_THUMBS;

    mediaRoot.style.setProperty('--featured-product-thumb-size', `${thumbSize}px`);

    return {
      scroller,
      items,
      gap,
      thumbSize,
      step: thumbSize + gap,
    };
  };

  const scrollFeaturedProductThumbs = (sectionRoot, activeIndex) => {
    const metrics = getFeaturedProductThumbMetrics(sectionRoot);
    if (!metrics) return;

    const { scroller, items, step } = metrics;
    const index = parseInt(activeIndex, 10);

    if (Number.isNaN(index) || index < 0 || !items[index]) return;

    sectionRoot.querySelectorAll('.featured-product__media [data-product-thumb]').forEach((thumb) => {
      const thumbIndex = parseInt(thumb.getAttribute('data-index'), 10);
      thumb.classList.toggle('is-active', thumbIndex === index);
    });

    if (items.length <= VISIBLE_THUMBS) {
      scroller.scrollTo({ left: 0, behavior: 'smooth' });
      return;
    }

    const scrollIndex = Math.min(index, items.length - VISIBLE_THUMBS);
    const scrollLeft = scrollIndex * step;

    scroller.scrollTo({
      left: scrollLeft,
      behavior: 'smooth',
    });
  };

  const queueFeaturedProductThumbSync = (sectionRoot, index) => {
    window.requestAnimationFrame(() => {
      scrollFeaturedProductThumbs(sectionRoot, index);
      window.requestAnimationFrame(() => {
        scrollFeaturedProductThumbs(sectionRoot, index);
      });
    });
  };

  const initFeaturedProductNoVariantUrl = () => {
    if (window.__featuredProductUrlGuardInit) return;
    window.__featuredProductUrlGuardInit = true;

    const stripVariantFromUrl = (urlString) => {
      const url = new URL(urlString, window.location.origin);
      url.searchParams.delete('variant');
      return url.pathname + url.search + url.hash;
    };

    const originalReplaceState = history.replaceState.bind(history);
    history.replaceState = function(state, title, url) {
      if (typeof url === 'string' && url.includes('variant=') && document.querySelector('.featured-product-section')) {
        url = stripVariantFromUrl(url);
      }
      return originalReplaceState(state, title, url);
    };

    const originalPushState = history.pushState.bind(history);
    history.pushState = function(state, title, url) {
      if (typeof url === 'string' && url.includes('variant=') && document.querySelector('.featured-product-section')) {
        url = stripVariantFromUrl(url);
      }
      return originalPushState(state, title, url);
    };

    if (window.location.search.includes('variant=')) {
      originalReplaceState({}, '', stripVariantFromUrl(window.location.href));
    }
  };

  const initFeaturedProductFlavorDropdown = (sectionRoot) => {
    const flavorWrapper = sectionRoot.querySelector('.variant_name-select-flavor.custom_default_variant');
    if (!flavorWrapper || flavorWrapper.dataset.featuredFlavorDropdownInit === 'true') return;

    flavorWrapper.dataset.featuredFlavorDropdownInit = 'true';

    const dropdownCard = flavorWrapper.querySelector('.featured-product-flavor-dropdown');
    const optionsList = flavorWrapper.querySelector('.selection_list_box');

    if (!dropdownCard || !optionsList) return;

    optionsList.style.display = '';

    const syncDropdownFromSelection = () => {
      const checkedInput = flavorWrapper.querySelector('[data-handle="select-flavor"] [type="radio"]:checked');
      if (!checkedInput) return;

      const variantInput = checkedInput.closest('.variant-input');
      const label = variantInput?.querySelector('label');
      const title = label?.querySelector('.label_value')?.textContent?.trim();
      const image = label?.querySelector('img')?.getAttribute('src');

      if (title) {
        dropdownCard.querySelector('.flavor_option_name span').textContent = title;
      }

      const imageEl = dropdownCard.querySelector('.flavor_option_image img');
      if (imageEl && image) {
        imageEl.setAttribute('src', image);
        imageEl.closest('.flavor_option_image').style.display = '';
      }
    };

    const openDropdown = () => {
      optionsList.style.removeProperty('display');
      optionsList.classList.add('is-open');
      dropdownCard.classList.add('active_flavor');
    };

    const closeDropdown = () => {
      optionsList.classList.remove('is-open');
      dropdownCard.classList.remove('active_flavor');
    };

    dropdownCard.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      if (optionsList.classList.contains('is-open')) {
        closeDropdown();
      } else {
        openDropdown();
      }
    });

    optionsList.addEventListener('click', (event) => {
      event.stopPropagation();
    });

    optionsList.querySelectorAll('[type="radio"]').forEach((input) => {
      input.addEventListener('change', () => {
        syncDropdownFromSelection();
        closeDropdown();
        window.setTimeout(() => {
          optionsList.style.removeProperty('display');
        }, 0);
      });
    });

    flavorWrapper.querySelectorAll('.selection_list_box label').forEach((label) => {
      label.addEventListener('click', (event) => {
        event.stopPropagation();
        window.setTimeout(() => {
          syncDropdownFromSelection();
          closeDropdown();
          optionsList.style.removeProperty('display');
        }, 50);
      });
    });

    document.addEventListener('click', (event) => {
      if (!flavorWrapper.contains(event.target)) {
        closeDropdown();
      }
    });

    syncDropdownFromSelection();
  };

  const initFeaturedProductMedia = (sectionRoot) => {
    const mediaRoot = sectionRoot.querySelector('.featured-product__media');
    if (!mediaRoot) return false;

    const slideshow = mediaRoot.querySelector('.product-image-slider .product-slideshow');
    if (!slideshow || typeof Flickity === 'undefined') return false;

    let flkty = Flickity.data(slideshow);

    if (!flkty && typeof theme !== 'undefined' && theme.Slideshow) {
      const thumbSlider = mediaRoot.querySelector('[data-product-thumbs]');
      const activeSlide = slideshow.querySelector('.starting-slide');
      const initialIndex = activeSlide ? parseInt(activeSlide.getAttribute('data-index'), 10) || 0 : 0;

      new theme.Slideshow(slideshow, {
        dragThreshold: 25,
        adaptiveHeight: true,
        avoidReflow: true,
        initialIndex: initialIndex,
        childNav: thumbSlider,
        childNavScroller: null,
        childVertical: false,
        fade: false,
        pageDots: false,
        wrapAround: true,
      });
      flkty = Flickity.data(slideshow);
    }

    if (!flkty) {
      flkty = new Flickity(slideshow, {
        pageDots: false,
        prevNextButtons: false,
        wrapAround: true,
        adaptiveHeight: true,
      });
    }

    if (!flkty) return false;

    if (sectionRoot.dataset.featuredProductMediaInit === 'true') {
      return true;
    }

    sectionRoot.dataset.featuredProductMediaInit = 'true';

    const syncThumbsFromIndex = (index) => {
      queueFeaturedProductThumbSync(sectionRoot, index);
      window.setTimeout(() => {
        scrollFeaturedProductThumbs(sectionRoot, index);
      }, 50);
    };

    flkty.on('change', syncThumbsFromIndex);
    flkty.on('settle', syncThumbsFromIndex);

    mediaRoot.querySelectorAll('[data-product-thumb]').forEach((thumb) => {
      thumb.addEventListener('click', (event) => {
        event.preventDefault();
        const index = parseInt(thumb.getAttribute('data-index'), 10);
        if (!Number.isNaN(index)) {
          flkty.select(index);
        }
      });
    });

    sectionRoot.featuredProductResizeHandler = () => {
      scrollFeaturedProductThumbs(sectionRoot, flkty.selectedIndex);
    };
    window.addEventListener('resize', sectionRoot.featuredProductResizeHandler);

    queueFeaturedProductThumbSync(sectionRoot, flkty.selectedIndex);

    if (window.jQuery && sectionRoot.querySelector('.custom_default_variant [data-handle="select-flavor"]')) {
      const selectedRadio = window.jQuery(sectionRoot)
        .find('.custom_default_variant [data-handle="select-flavor"] [type="radio"]:checked')
        .attr('data-media-id');

      if (selectedRadio !== undefined) {
        const thumbItem = window.jQuery(sectionRoot)
          .find('.featured-product__media .product__thumb[data-id="' + selectedRadio + '"]');

        if (thumbItem.length > 0) {
          const index = parseInt(thumbItem.attr('data-index'), 10);
          if (!Number.isNaN(index) && flkty.selectedIndex !== index) {
            flkty.select(index);
          }
        }
      }
    }

    return true;
  };

  const initFeaturedProductDiscount = (sectionRoot) => {
    const updateDiscountPercentage = () => {
      const compareEl = sectionRoot.querySelector('[data-product-price-compare]');
      const priceEl = sectionRoot.querySelector('[data-product-price]');
      const discountEl = sectionRoot.querySelector('.discount-percent');
      const priceWrapper = sectionRoot.querySelector('.data-price-wrapper');

      if (!compareEl || !priceEl) return;

      const comparePrice = parseFloat(compareEl.textContent.replace(/[^0-9.]/g, ''));
      const currentPrice = parseFloat(priceEl.textContent.replace(/[^0-9.]/g, ''));

      if (comparePrice && currentPrice && comparePrice > currentPrice && priceWrapper && !priceWrapper.classList.contains('hide')) {
        const discountPercent = Math.round(((comparePrice - currentPrice) / comparePrice) * 100);
        const pricesContainer = sectionRoot.querySelector('.product-single__prices');

        if (discountEl) {
          discountEl.textContent = 'Save ' + discountPercent + '%';
          discountEl.style.display = 'inline-block';
        } else if (pricesContainer && window.jQuery) {
          window.jQuery(pricesContainer).append(
            '<span class="discount-percent" style="display: inline-block;">Save ' + discountPercent + '%</span>'
          );
        }
      } else if (discountEl) {
        discountEl.style.display = 'none';
      }
    };

    if (sectionRoot.dataset.featuredProductDiscountInit === 'true') {
      updateDiscountPercentage();
      return;
    }

    sectionRoot.dataset.featuredProductDiscountInit = 'true';

    sectionRoot.querySelectorAll('.variant-wrapper .variant-input').forEach((variant) => {
      variant.addEventListener('click', () => {
        setTimeout(updateDiscountPercentage, 100);
      });
    });

    sectionRoot.querySelectorAll('.product-info-main .select-quantity .s_q_card').forEach((variant) => {
      variant.addEventListener('click', () => {
        setTimeout(updateDiscountPercentage, 100);
      });
    });

    updateDiscountPercentage();
  };

  const initFeaturedProductSection = (sectionRoot) => {
    if (!sectionRoot) return;

    initFeaturedProductMedia(sectionRoot);
    initFeaturedProductFlavorDropdown(sectionRoot);
    initFeaturedProductDiscount(sectionRoot);
  };

  const initAllFeaturedProductSections = () => {
    initFeaturedProductNoVariantUrl();

    document.querySelectorAll('[data-js-featured-product-section]').forEach((anchor) => {
      const sectionRoot = document.getElementById('shopify-section-' + anchor.getAttribute('data-js-featured-product-section'));
      initFeaturedProductSection(sectionRoot);
    });
  };

  const retryFeaturedProductMediaInit = () => {
    document.querySelectorAll('[data-js-featured-product-section]').forEach((anchor) => {
      const sectionRoot = document.getElementById('shopify-section-' + anchor.getAttribute('data-js-featured-product-section'));
      if (sectionRoot) {
        initFeaturedProductMedia(sectionRoot);
      }
    });
  };

  const scheduleFeaturedProductInit = () => {
    if (window.__featuredProductInitScheduled) return;
    window.__featuredProductInitScheduled = true;

    initAllFeaturedProductSections();

    let attempts = 0;
    const retryInterval = window.setInterval(() => {
      retryFeaturedProductMediaInit();
      attempts += 1;

      const hasPendingMedia = Array.from(document.querySelectorAll('[data-js-featured-product-section]')).some((anchor) => {
        const sectionRoot = document.getElementById('shopify-section-' + anchor.getAttribute('data-js-featured-product-section'));
        return sectionRoot
          && sectionRoot.querySelector('.featured-product__media .product-image-slider')
          && sectionRoot.dataset.featuredProductMediaInit !== 'true';
      });

      if (!hasPendingMedia || attempts >= 20) {
        window.clearInterval(retryInterval);
      }
    }, 100);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scheduleFeaturedProductInit, { once: true });
  } else {
    scheduleFeaturedProductInit();
  }

  document.addEventListener('page:loaded', scheduleFeaturedProductInit, { once: true });

  document.addEventListener('shopify:section:load', (event) => {
    const sectionRoot = event.target;
    if (!sectionRoot || !sectionRoot.querySelector('[data-js-featured-product-section]')) {
      return;
    }

    if (sectionRoot.featuredProductResizeHandler) {
      window.removeEventListener('resize', sectionRoot.featuredProductResizeHandler);
      delete sectionRoot.featuredProductResizeHandler;
    }

    delete sectionRoot.dataset.featuredProductMediaInit;
    delete sectionRoot.dataset.featuredProductDiscountInit;

    const flavorWrapper = sectionRoot.querySelector('.variant_name-select-flavor.custom_default_variant');
    if (flavorWrapper) {
      delete flavorWrapper.dataset.featuredFlavorDropdownInit;
    }

    initFeaturedProductSection(sectionRoot);
  });
})();
